// src/components/pdf-viewer/NarrationPlayer.tsx
//
// In-page narration player for the PDF viewer. Plays per-section MP3s served
// from GCS, advances the PDF page as the audio progresses through a section's
// pages, and exposes the active caption text (current section content) to its
// parent so captions can be rendered overlaying the PDF.
//
// Owns the <audio> element, current section index, elapsed time, isPlaying.
// Exposes pause() via an imperative ref so the parent can stop playback when
// the user scrolls the PDF manually.
//
// No new libraries — plain HTMLAudioElement, plain React.
import React, {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from 'react';
import { Button, Slider, Tooltip } from 'antd';
import {
  PlayCircleFilled,
  PauseCircleFilled,
  StepForwardOutlined,
  StepBackwardOutlined,
  SoundOutlined,
  SoundFilled,
} from '@ant-design/icons';

export interface NarrationSection {
  name: string;
  content: string;
  audio_url: string;
  duration_seconds: number;
  pages: number[]; // sorted, deduped
}

export interface NarrationPlayerHandle {
  pause: () => void;
  play: () => void;
}

interface NarrationPlayerProps {
  sections: NarrationSection[];
  /** Reserved for future use — currently the player no longer auto-changes
   *  pages on section transitions (the section.pages array points at image
   *  pages, not header pages, so it's not safe to use as a nav target). */
  onPageChange?: (page: number) => void;
  /** Called when caption text, play state, or caption visibility changes.
   *  `isPlaying` reflects audio playback only; `captionsVisible` is true when
   *  the user has captions ON and audio is playing. */
  onCaptionUpdate?: (
    caption: string,
    isPlaying: boolean,
    captionsVisible: boolean,
  ) => void;
  /** True when the player should render in compact (mobile) layout */
  isMobile?: boolean;
  /** Optional element rendered inside the sticky bar (e.g. PDF search box).
   *  On desktop it sits to the right of the player controls; on mobile it
   *  wraps onto its own row thanks to the bar's flexWrap. */
  rightSlot?: React.ReactNode;
}

const formatTime = (sec: number): string => {
  if (!isFinite(sec) || sec < 0) sec = 0;
  const m = Math.floor(sec / 60);
  const s = Math.floor(sec % 60);
  return `${m}:${s.toString().padStart(2, '0')}`;
};

export const NarrationPlayer = forwardRef<NarrationPlayerHandle, NarrationPlayerProps>(
  ({ sections, onPageChange, onCaptionUpdate, isMobile = false, rightSlot }, ref) => {
    const audioRef = useRef<HTMLAudioElement | null>(null);
    const [sectionIdx, setSectionIdx] = useState(0);
    const [isPlaying, setIsPlaying] = useState(false);
    const [elapsed, setElapsed] = useState(0); // seconds within current section
    const [hasInteracted, setHasInteracted] = useState(false);
    // Volume 0–1. Persisted in localStorage so the user's preference sticks
    // across sessions and across re-mounts of the modal.
    const [volume, setVolume] = useState<number>(() => {
      const stored = typeof window !== 'undefined'
        ? window.localStorage.getItem('spectreviews:narrationVolume')
        : null;
      const parsed = stored !== null ? parseFloat(stored) : NaN;
      return isFinite(parsed) ? Math.max(0, Math.min(1, parsed)) : 0.9;
    });
    const [muted, setMuted] = useState(false);
    const preMuteVolumeRef = useRef<number>(0.9);
    // Captions on/off — persisted so the user's preference sticks across
    // sessions. Defaults to ON (captions visible while playing).
    const [ccEnabled, setCcEnabled] = useState<boolean>(() => {
      const stored = typeof window !== 'undefined'
        ? window.localStorage.getItem('spectreviews:narrationCaptionsEnabled')
        : null;
      return stored === null ? true : stored === 'true';
    });
    useEffect(() => {
      try {
        window.localStorage.setItem(
          'spectreviews:narrationCaptionsEnabled',
          String(ccEnabled),
        );
      } catch {
        /* storage may be unavailable */
      }
    }, [ccEnabled]);
    // When the previous section ended naturally, the browser fires `ended`
    // and then `pause`, which races with our `setSectionIdx` re-render and
    // flips `isPlaying` to false before the load effect runs. Use a ref to
    // signal the load effect to autoplay the next section regardless.
    const autoPlayNextRef = useRef(false);

    const safeSections = useMemo(
      () => sections.filter((s) => s && s.audio_url),
      [sections],
    );
    const currentSection = safeSections[sectionIdx];
    const sectionDuration = currentSection?.duration_seconds || 0;

    // Imperative API: parent can pause/resume the player from outside
    useImperativeHandle(
      ref,
      () => ({
        pause: () => {
          const a = audioRef.current;
          if (a && !a.paused) {
            a.pause();
          }
        },
        play: () => {
          const a = audioRef.current;
          if (!a) return;
          setHasInteracted(true);
          a.play().catch(() => {
            setIsPlaying(false);
          });
        },
      }),
      [],
    );

    // When section changes, reset audio and load the new src.
    //
    // We also fire `onPageChange(firstPage)` on actual section transitions
    // (skipping the initial mount). The parent decides what to do with it —
    // currently it acts only on the FIRST transition (jump out of intro into
    // the report body) and ignores all later transitions.
    const isFirstMountRef = useRef(true);
    useEffect(() => {
      const a = audioRef.current;
      if (!a || !currentSection) return;
      // Only reset src if it actually changed (avoid reload loops)
      const targetSrc = currentSection.audio_url;
      if (!a.src.endsWith(targetSrc.split('/').pop() || '')) {
        a.src = targetSrc;
        a.currentTime = 0;
        setElapsed(0);
        // Autoplay next section when handoff was triggered by `ended`, OR
        // when we're currently playing and the user moved sections.
        if (autoPlayNextRef.current || isPlaying) {
          autoPlayNextRef.current = false;
          a.play().catch(() => {
            setIsPlaying(false);
          });
        }
      }
      if (isFirstMountRef.current) {
        isFirstMountRef.current = false;
      } else if (onPageChange) {
        const firstPage = currentSection.pages?.[0];
        if (firstPage) onPageChange(firstPage);
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [sectionIdx]);

    // Sync volume / muted state to the underlying <audio> element and persist.
    useEffect(() => {
      const a = audioRef.current;
      if (!a) return;
      a.volume = muted ? 0 : volume;
      a.muted = muted;
      try {
        window.localStorage.setItem(
          'spectreviews:narrationVolume',
          String(volume),
        );
      } catch {
        /* storage may be unavailable (private mode, quota) */
      }
    }, [volume, muted]);

    const handleVolumeChange = useCallback((v: number) => {
      const clamped = Math.max(0, Math.min(1, v / 100));
      setVolume(clamped);
      if (clamped > 0 && muted) setMuted(false);
    }, [muted]);

    const toggleMute = useCallback(() => {
      if (muted) {
        setMuted(false);
        if (volume === 0) setVolume(preMuteVolumeRef.current || 0.9);
      } else {
        preMuteVolumeRef.current = volume || 0.9;
        setMuted(true);
      }
    }, [muted, volume]);

    // Notify parent of caption changes. Captions are visible only when
    // playing AND the user hasn't toggled them off via the CC button.
    useEffect(() => {
      if (onCaptionUpdate) {
        onCaptionUpdate(
          currentSection?.content || '',
          isPlaying,
          isPlaying && ccEnabled,
        );
      }
    }, [currentSection, isPlaying, ccEnabled, onCaptionUpdate]);

    // Audio event handlers
    const handlePlay = useCallback(() => setIsPlaying(true), []);
    const handlePause = useCallback(() => setIsPlaying(false), []);
    const handleTimeUpdate = useCallback(() => {
      const a = audioRef.current;
      if (!a) return;
      setElapsed(a.currentTime);
    }, []);
    const handleEnded = useCallback(() => {
      // Auto-advance to next section, or stop gracefully on the last
      if (sectionIdx < safeSections.length - 1) {
        autoPlayNextRef.current = true;
        setSectionIdx(sectionIdx + 1);
      } else {
        setIsPlaying(false);
        setElapsed(sectionDuration);
      }
    }, [sectionIdx, safeSections.length, sectionDuration]);

    const togglePlay = useCallback(() => {
      const a = audioRef.current;
      if (!a) return;
      setHasInteracted(true);
      if (a.paused) {
        a.play().catch((err) => {
          console.error('[NarrationPlayer] play() failed:', err);
          setIsPlaying(false);
        });
      } else {
        a.pause();
      }
    }, []);

    const goPrev = useCallback(() => {
      if (sectionIdx > 0) {
        setSectionIdx(sectionIdx - 1);
      } else {
        // Restart current section from the top
        const a = audioRef.current;
        if (a) a.currentTime = 0;
      }
    }, [sectionIdx]);

    const goNext = useCallback(() => {
      if (sectionIdx < safeSections.length - 1) {
        setSectionIdx(sectionIdx + 1);
      }
    }, [sectionIdx, safeSections.length]);

    if (!safeSections.length) return null;

    // Sticky bottom bar styles — vertical container with three rows:
    //   Row 1: prev/play/next + section label + volume control
    //   Row 2: progress bar + elapsed / duration
    //   Row 3: rightSlot (search box) — full-width
    const barStyle: React.CSSProperties = {
      position: 'sticky',
      bottom: 0,
      zIndex: 30,
      backgroundColor: 'rgba(20, 20, 20, 0.92)',
      backdropFilter: 'blur(8px)',
      WebkitBackdropFilter: 'blur(8px)',
      color: '#fff',
      padding: isMobile ? '6px 10px' : '8px 16px',
      display: 'flex',
      flexDirection: 'column',
      gap: isMobile ? 6 : 8,
      borderTop: '1px solid rgba(255,255,255,0.1)',
      boxShadow: '0 -4px 14px rgba(0,0,0,0.25)',
    };

    const rowStyle: React.CSSProperties = {
      display: 'flex',
      alignItems: 'center',
      gap: isMobile ? 6 : 10,
      width: '100%',
    };

    const progressPct = sectionDuration > 0
      ? Math.min(100, (elapsed / sectionDuration) * 100)
      : 0;

    // CC toggle button — same on mobile and desktop. The button shape
    // matches a YouTube-style CC pill with a subtle border when active.
    const ccButton = (
      <Button
        type="text"
        size="small"
        onClick={() => setCcEnabled((v) => !v)}
        aria-label={ccEnabled ? 'Hide captions' : 'Show captions'}
        style={{
          color: ccEnabled ? '#fff' : 'rgba(255,255,255,0.55)',
          fontWeight: 700,
          fontSize: 12,
          letterSpacing: 0.5,
          padding: '0 8px',
          height: 24,
          lineHeight: '20px',
          border: `1.5px solid ${ccEnabled ? '#fff' : 'rgba(255,255,255,0.4)'}`,
          borderRadius: 4,
        }}
      >
        CC
      </Button>
    );

    const playButton = (
      <Button
        type="text"
        size="large"
        icon={
          isPlaying ? (
            <PauseCircleFilled style={{ color: '#fff', fontSize: 30 }} />
          ) : (
            <PlayCircleFilled style={{ color: '#fff', fontSize: 30 }} />
          )
        }
        onClick={(e) => {
          (e.currentTarget as HTMLButtonElement).blur();
          togglePlay();
        }}
        aria-label={isPlaying ? 'Pause' : 'Play'}
      />
    );

    const volumeControl = (
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 6,
          width: isMobile ? 110 : 140,
          flex: '0 0 auto',
        }}
      >
        <Tooltip title={muted || volume === 0 ? 'Unmute' : 'Mute'}>
          <Button
            type="text"
            size="small"
            onClick={toggleMute}
            icon={
              muted || volume === 0 ? (
                <SoundOutlined style={{ color: 'rgba(255,255,255,0.55)', fontSize: 16 }} />
              ) : (
                <SoundFilled style={{ color: '#fff', fontSize: 16 }} />
              )
            }
            aria-label={muted ? 'Unmute' : 'Mute'}
          />
        </Tooltip>
        <Slider
          min={0}
          max={100}
          step={1}
          value={muted ? 0 : Math.round(volume * 100)}
          onChange={handleVolumeChange}
          tooltip={{ formatter: (v) => `${v}%` }}
          style={{ flex: 1, margin: 0 }}
          trackStyle={{ backgroundColor: '#1890ff' }}
          railStyle={{ backgroundColor: 'rgba(255,255,255,0.18)' }}
        />
      </div>
    );

    const sectionLabel = (
      <div
        style={{
          flex: 1,
          minWidth: 0,
          fontSize: isMobile ? 12 : 13,
          fontWeight: 500,
          whiteSpace: 'nowrap',
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          display: 'flex',
          alignItems: 'center',
          gap: 6,
        }}
      >
        <SoundOutlined style={{ color: '#9ad7ff', flexShrink: 0 }} />
        <span style={{ color: '#9ad7ff', flexShrink: 0 }}>
          Section {sectionIdx + 1} of {safeSections.length}:
        </span>
        <span style={{ overflow: 'hidden', textOverflow: 'ellipsis' }}>
          {currentSection?.name || ''}
        </span>
      </div>
    );

    const timeText = (
      <div
        style={{
          fontSize: 12,
          opacity: 0.85,
          fontVariantNumeric: 'tabular-nums',
          flexShrink: 0,
          marginLeft: 8,
        }}
      >
        {formatTime(elapsed)} / {formatTime(sectionDuration)}
      </div>
    );

    const progressBar = (
      <div
        style={{
          position: 'relative',
          flex: 1,
          height: 4,
          backgroundColor: 'rgba(255,255,255,0.18)',
          borderRadius: 2,
          overflow: 'hidden',
        }}
      >
        <div
          style={{
            position: 'absolute',
            inset: 0,
            width: `${progressPct}%`,
            backgroundColor: '#1890ff',
            transition: 'width 0.2s linear',
          }}
        />
      </div>
    );

    return (
      <>
        {/* Hidden audio element */}
        <audio
          ref={audioRef}
          onPlay={handlePlay}
          onPause={handlePause}
          onTimeUpdate={handleTimeUpdate}
          onEnded={handleEnded}
          preload="metadata"
        />

        {/* Sticky bottom narration bar */}
        <div style={barStyle} data-narration-player>
          {isMobile ? (
            <>
              {/* Thin section-progress bar at the top of the mobile player */}
              <div
                style={{
                  position: 'relative',
                  width: '100%',
                  height: 3,
                  backgroundColor: 'rgba(255,255,255,0.18)',
                  borderRadius: 2,
                  overflow: 'hidden',
                  flex: '0 0 auto',
                }}
              >
                <div
                  style={{
                    position: 'absolute',
                    inset: 0,
                    width: `${progressPct}%`,
                    backgroundColor: '#1890ff',
                    transition: 'width 0.2s linear',
                  }}
                />
              </div>

              {/* Row 1: volume | prev | play | next | CC */}
              <div style={{ ...rowStyle, justifyContent: 'space-between' }}>
                {volumeControl}
                <Button
                  type="text"
                  size="small"
                  icon={<StepBackwardOutlined style={{ color: '#fff', fontSize: 18 }} />}
                  onClick={goPrev}
                  disabled={sectionIdx === 0 && elapsed < 1}
                  aria-label="Previous section"
                />
                {playButton}
                <Button
                  type="text"
                  size="small"
                  icon={<StepForwardOutlined style={{ color: '#fff', fontSize: 18 }} />}
                  onClick={goNext}
                  disabled={sectionIdx >= safeSections.length - 1}
                  aria-label="Next section"
                />
                {ccButton}
              </div>

              {/* Row 2: section title + elapsed/duration */}
              <div style={rowStyle}>
                {sectionLabel}
                {timeText}
              </div>
            </>
          ) : (
            <>
              {/* Desktop Row 1: prev/play/next + section label + volume + CC */}
              <div style={rowStyle}>
                <Tooltip title="Previous section">
                  <Button
                    type="text"
                    size="small"
                    icon={<StepBackwardOutlined style={{ color: '#fff', fontSize: 18 }} />}
                    onClick={goPrev}
                    disabled={sectionIdx === 0 && elapsed < 1}
                  />
                </Tooltip>
                {playButton}
                <Tooltip title="Next section">
                  <Button
                    type="text"
                    size="small"
                    icon={<StepForwardOutlined style={{ color: '#fff', fontSize: 18 }} />}
                    onClick={goNext}
                    disabled={sectionIdx >= safeSections.length - 1}
                  />
                </Tooltip>
                {sectionLabel}
                {volumeControl}
                {ccButton}
              </div>

              {/* Desktop Row 2: progress bar + elapsed/duration */}
              <div style={rowStyle}>
                {progressBar}
                <div
                  style={{
                    fontSize: 12,
                    opacity: 0.85,
                    fontVariantNumeric: 'tabular-nums',
                    minWidth: 80,
                    textAlign: 'right',
                    flexShrink: 0,
                  }}
                >
                  {formatTime(elapsed)} / {formatTime(sectionDuration)}
                </div>
              </div>
            </>
          )}

          {/* Row 3 (both): search box (rightSlot) — full-width on its own row. */}
          {rightSlot && (
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                width: '100%',
                paddingTop: 4,
                borderTop: '1px solid rgba(255,255,255,0.1)',
              }}
            >
              {rightSlot}
            </div>
          )}

          {!hasInteracted && !isPlaying && !isMobile && (
            <div
              style={{
                fontSize: 11,
                opacity: 0.6,
                width: '100%',
                textAlign: 'center',
              }}
            >
              Press play to listen along with the report
            </div>
          )}
        </div>
      </>
    );
  },
);

NarrationPlayer.displayName = 'NarrationPlayer';

/**
 * Captions overlay — rendered separately so the parent can position it inside
 * the PDF render area, above the sticky bottom bar.
 */
interface NarrationCaptionsProps {
  text: string;
  visible: boolean;
  isMobile?: boolean;
}

export const NarrationCaptions: React.FC<NarrationCaptionsProps> = ({
  text,
  visible,
  isMobile = false,
}) => {
  if (!visible || !text) return null;
  // Mobile gets ~35% more vertical room: 7 lines vs 5.
  const lineClamp = isMobile ? 7 : 5;
  return (
    <div
      style={{
        maxWidth: '80%',
        backgroundColor: 'rgba(0,0,0,0.58)',
        color: '#fff',
        padding: '10px 16px',
        borderRadius: 8,
        fontSize: 15,
        lineHeight: 1.45,
        textAlign: 'center',
        boxShadow: '0 4px 14px rgba(0,0,0,0.3)',
        display: '-webkit-box',
        WebkitLineClamp: lineClamp,
        WebkitBoxOrient: 'vertical',
        overflow: 'hidden',
        pointerEvents: 'none',
      }}
    >
      {text}
    </div>
  );
};
